import {
  Box,
  IconButton,
  InputBase,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FlagIcon from '@mui/icons-material/Flag';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import EventIcon from '@mui/icons-material/Event';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import AdjustIcon from '@mui/icons-material/Adjust';
import RemoveDoneIcon from '@mui/icons-material/RemoveDone';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { tokens } from '../../../../global/theme/tokens';
import EmployeesMultiSelect from '../../../select/EmployeesMultiSelect';

/**
 * Assignee and sorting filters for the whole task view.
 * The values end up as query params of work-packages/{id}/tasks.
 */
const TaskFilterBar = ({ users = [], filter, onChange }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const { sortBy, userIds, withoutUsers, hideDone, hideEmpty } = filter;

  const [searchInput, setSearchInput] = useState(filter.search || '');

  // typing should not fire a request per keystroke
  useEffect(() => {
    const timeout = setTimeout(() => {
      if ((filter.search || '') !== searchInput)
        onChange({ ...filter, search: searchInput });
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchInput, filter]);

  function handleSearchClear() {
    setSearchInput('');
  }

  function handleSortChange(e, value) {
    onChange({ ...filter, sortBy: value === 'manual' ? null : value });
  }

  return (
    <Box
      className="flex flex-col md:flex-row md:items-center justify-between gap-2 rounded-xl px-3 py-2 backdrop-blur-lg"
      sx={{ backgroundColor: colors.glass }}
    >
      <Box className="flex flex-col md:flex-row md:items-center gap-2 grow min-w-0">
        <Box
          className="flex items-center gap-1 rounded-lg px-2 w-full md:max-w-[220px]"
          sx={{
            backgroundColor: colors.bgInput,
            backdropFilter: 'blur(10px)',
            border: `1px solid ${colors.text}20`,
            ':focus-within': { borderColor: `${colors.info}80` },
          }}
        >
          <SearchIcon sx={{ fontSize: '16px', opacity: 0.6 }} />
          <InputBase
            value={searchInput}
            placeholder={t('Search task...')}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') handleSearchClear();
            }}
            sx={{ fontSize: '13px', width: '100%' }}
          />
          {searchInput && (
            <IconButton size="small" onClick={handleSearchClear}>
              <CloseIcon sx={{ fontSize: '15px' }} />
            </IconButton>
          )}
        </Box>
        <EmployeesMultiSelect
          users={users}
          selectedIds={userIds}
          // the API filters either by users or by the missing assignee
          onChange={(ids) =>
            onChange({ ...filter, userIds: ids, withoutUsers: false })
          }
          withUnassigned
          unassignedSelected={withoutUsers}
          onUnassignedChange={(value) =>
            onChange({ ...filter, withoutUsers: value, userIds: [] })
          }
        />

        {/* finished tasks stay hidden until they are asked for */}
        <ToggleButton
          size="small"
          value="hideDone"
          selected={Boolean(hideDone)}
          onChange={() => onChange({ ...filter, hideDone: !hideDone })}
          className="flex gap-1 shrink-0"
          sx={{ padding: '3px 8px' }}
        >
          <RemoveDoneIcon sx={{ fontSize: '16px' }} />
          <Typography fontSize="12px" className="hidden sm:block">
            {t('Hide done')}
          </Typography>
        </ToggleButton>

        {/* phases and work packages with nothing to list stay out of the way */}
        <Tooltip title={t('Hide phases and work packages without tasks')}>
          <ToggleButton
            size="small"
            value="hideEmpty"
            selected={Boolean(hideEmpty)}
            onChange={() => onChange({ ...filter, hideEmpty: !hideEmpty })}
            className="flex gap-1 shrink-0"
            sx={{ padding: '3px 8px' }}
          >
            <VisibilityOffIcon sx={{ fontSize: '16px' }} />
            <Typography fontSize="12px" className="hidden sm:block">
              {t('Hide empty')}
            </Typography>
          </ToggleButton>
        </Tooltip>
      </Box>

      <Box className="flex items-center gap-2 shrink-0 overflow-x-auto">
        <Typography
          fontSize="12px"
          className="opacity-60 whitespace-nowrap hidden lg:block"
        >
          {t('Sort tasks')}
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={sortBy || 'manual'}
          onChange={handleSortChange}
          className="backdrop-blur-lg"
        >
          <ToggleButton value="manual" className="flex gap-1">
            <DragHandleIcon sx={{ fontSize: '16px' }} />
            <Typography fontSize="12px" className="hidden sm:block">
              {t('Manual')}
            </Typography>
          </ToggleButton>
          <ToggleButton value="priority" className="flex gap-1">
            <FlagIcon sx={{ fontSize: '16px' }} />
            <Typography fontSize="12px" className="hidden sm:block">
              {t('Priority')}
            </Typography>
          </ToggleButton>
          <ToggleButton value="deadline" className="flex gap-1">
            <EventIcon sx={{ fontSize: '16px' }} />
            <Typography fontSize="12px" className="hidden sm:block">
              {t('Deadline')}
            </Typography>
          </ToggleButton>
          <ToggleButton value="effortScore" className="flex gap-1">
            <AdjustIcon sx={{ fontSize: '16px' }} />
            <Typography fontSize="12px" className="hidden sm:block">
              {t('Points')}
            </Typography>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
    </Box>
  );
};

export default TaskFilterBar;
